import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Share2, Loader2, Copy, ExternalLink, ShieldOff, CalendarClock, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { EVIDENCE } from "@/constants/testIds";
import { createShare, listShares, revokeShare, publicShareUrl } from "@/lib/evidence";

const SEMA_PRIMARY = "#1F3A93";

const EXPIRY_OPTIONS = [
  { value: 1, label: "1 day" },
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = then - Date.now();
  const days = Math.round(diff / (1000 * 60 * 60 * 24));
  if (days > 1) return `in ${days} days`;
  if (days === 1) return "tomorrow";
  if (days === 0) return "today";
  if (days === -1) return "yesterday";
  return `${Math.abs(days)} days ago`;
}

function StatusPill({ status }) {
  const map = {
    active: { label: "ACTIVE", cls: "border-emerald-500/40 text-emerald-500" },
    expired: { label: "EXPIRED", cls: "border-amber-500/40 text-amber-500" },
    revoked: { label: "REVOKED", cls: "border-red-500/40 text-red-500" },
  };
  const c = map[status] || map.expired;
  return (
    <Badge variant="outline" className={`rounded-full font-mono text-[10px] uppercase tracking-[0.16em] ${c.cls}`}>
      {c.label}
    </Badge>
  );
}

/**
 * `EvidenceShareDialog` — mint / list / revoke public share links.
 *
 * A share is an opaque token that maps to a stripped, read-only view of
 * the evidence pack (question, SQL, lineage, RBAC, timestamp, signature
 * badge) plus a public PDF export. Anyone with the URL can view; nobody
 * else. Every mint/access/revoke is logged in the audit trail.
 */
export function EvidenceShareDialog({ answerId, open, onOpenChange }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiryDays, setExpiryDays] = useState(7);

  useEffect(() => {
    if (!open || !answerId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await listShares(answerId);
        if (!cancelled) setShares(rows || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Could not load shares.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, answerId]);

  const active = shares.filter((s) => s.status === "active");
  const history = shares.filter((s) => s.status !== "active");

  const mint = async () => {
    setCreating(true);
    try {
      const share = await createShare(answerId, expiryDays);
      setShares((prev) => [share, ...prev]);
      const url = publicShareUrl(share.token);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Share link created — copied to clipboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(publicShareUrl(token));
      toast.success("Copied share URL");
    } catch {
      toast.error("Copy failed");
    }
  };

  const revoke = async (token) => {
    if (!confirm("Revoke this share link? Anyone with the URL will see a revoked page.")) return;
    try {
      const updated = await revokeShare(token);
      setShares((prev) => prev.map((s) => (s.token === token ? updated : s)));
      toast.success("Share link revoked.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={EVIDENCE.shareDialog}
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono text-sm">
            <Share2 className="h-4 w-4" style={{ color: SEMA_PRIMARY }} />
            Share evidence pack
          </DialogTitle>
        </DialogHeader>

        <div className="mt-1 rounded-lg border border-border/60 bg-background/40 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
              <label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                Expires in
              </label>
              <select
                data-testid={EVIDENCE.shareExpiry}
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <Button
              data-testid={EVIDENCE.shareCreateBtn}
              onClick={mint}
              disabled={creating}
              className="ml-auto h-9 rounded-full text-xs text-white hover:opacity-95"
              style={{ background: SEMA_PRIMARY }}
            >
              {creating ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Share2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Generate link
            </Button>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Anyone with the URL can view a read-only rendering of this evidence pack and export
            the PDF. No login required. Revoke anytime.
          </p>
        </div>

        <div className="mt-4">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading shares…
            </div>
          )}

          {!loading && active.length === 0 && history.length === 0 && (
            <div className="rounded-md border border-dashed border-border/50 p-6 text-center text-xs text-muted-foreground">
              No share links yet. Generate one above to invite an external reviewer.
            </div>
          )}

          {active.length > 0 && (
            <>
              <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                Active
              </div>
              <ul className="space-y-2">
                {active.map((s) => (
                  <li
                    key={s.id}
                    data-testid={EVIDENCE.shareRow}
                    className="rounded-md border border-border/40 bg-background/60 p-3 text-xs"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={s.status} />
                      <span className="text-[10px] text-muted-foreground">
                        expires {relTime(s.expires_at)}
                      </span>
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Eye className="h-3 w-3" /> {s.view_count} view(s)
                      </span>
                      <Button
                        onClick={() => copy(s.token)}
                        variant="ghost"
                        size="sm"
                        data-testid={EVIDENCE.shareCopy}
                        className="ml-auto h-7 gap-1 rounded-full text-[10px]"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </Button>
                      <a
                        href={publicShareUrl(s.token)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-7 items-center gap-1 rounded-full border border-border/60 px-2 text-[10px] text-foreground hover:bg-muted/60"
                      >
                        <ExternalLink className="h-3 w-3" /> Open
                      </a>
                      <Button
                        onClick={() => revoke(s.token)}
                        variant="ghost"
                        size="sm"
                        data-testid={EVIDENCE.shareRevoke}
                        className="h-7 gap-1 rounded-full text-[10px] text-red-500 hover:bg-red-500/10"
                      >
                        <ShieldOff className="h-3 w-3" /> Revoke
                      </Button>
                    </div>
                    <div
                      data-testid={EVIDENCE.shareUrl}
                      className="mt-2 truncate font-mono text-[10px] text-muted-foreground"
                    >
                      {publicShareUrl(s.token)}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {history.length > 0 && (
            <>
              <div className="mb-1 mt-4 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                Past shares
              </div>
              <ul className="space-y-1">
                {history.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center gap-2 rounded-md px-2 py-1 text-[11px] text-muted-foreground"
                  >
                    <StatusPill status={s.status} />
                    <span className="font-mono text-[10px]">
                      {(s.token || "").slice(0, 8)}…
                    </span>
                    <span className="ml-auto text-[10px]">
                      {s.view_count} view(s) · created {relTime(s.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
